import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Space, Input, Modal, Form, message, Popconfirm, Select, Tag, Card, Row, Col, Upload, InputNumber } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UploadOutlined, BarChartOutlined } from '@ant-design/icons';
import { benchmarkApi, businessScenarioApi, benchmarkMetricApi } from '../../services/api';
import { formatDateTime } from '../../utils/dateUtils';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const { Search, TextArea } = Input;
const { Option } = Select;

function BenchmarkList() {
  const navigate = useNavigate();
  const { currentScenario, hasManagePermission } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [businessScenarios, setBusinessScenarios] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    benchmarkType: undefined,
    businessScenarioId: undefined,
    benchmarkVersion: '',
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  
  // 附件上传相关状态
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    uploading: false,
    current: 0,
    total: 0,
    percent: 0
  });
  
  // 指标配置相关状态
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Benchmark类型
  const benchmarkTypes = ['基线benchmark', '样本验收benchmark', '线上周期评测benchmark'];

  // 模拟业务场景数据
  const mockBusinessScenarios = [
    { id: 1, scenarioName: '合同机审-类模板审查' },
    { id: 2, scenarioName: '合同机审-通用文本审查' },
    { id: 3, scenarioName: '文件机审-证照文件审查' },
    { id: 4, scenarioName: '要素抽取-合同关键信息' },
  ];

  // 模拟Benchmark数据
  const mockData = [
    {
      id: 1,
      benchmarkType: '基线benchmark',
      benchmarkVersion: 'V202401151200',
      useCaseCount: 150,
      businessScenarioId: 1,
      businessScenarioName: '合同机审-类模板审查',
      remark: '合同机审基线benchmark，用于POC阶段验证',
      metrics: [
        { metricName: '合同无风险精确率', metricValue: 0.95, metricType: 'judge' },
        { metricName: '合同无风险召回率', metricValue: 0.88, metricType: 'judge' },
        { metricName: '模版推荐准确率', metricValue: 0.92, metricType: 'process' }
      ],
      gmtCreate: '2024-01-15 12:00:00',
      creator: 'admin'
    },
    {
      id: 2,
      benchmarkType: '样本验收benchmark',
      benchmarkVersion: 'V202401141500',
      useCaseCount: 200,
      businessScenarioId: 1,
      businessScenarioName: '合同机审-类模板审查',
      remark: '第一次迭代验收benchmark',
      metrics: [
        { metricName: '合同无风险精确率', metricValue: 0.97, metricType: 'judge' },
        { metricName: '合同无风险召回率', metricValue: 0.90, metricType: 'judge' }
      ],
      gmtCreate: '2024-01-14 15:00:00',
      creator: 'zhangsan'
    },
    {
      id: 3,
      benchmarkType: '线上周期评测benchmark',
      benchmarkVersion: 'V202401131000',
      useCaseCount: 100,
      businessScenarioId: 1,
      businessScenarioName: '合同机审-类模板审查',
      remark: '2024年1月第二周线上评测benchmark',
      metrics: [
        { metricName: '合同无风险精确率', metricValue: 0.94, metricType: 'judge' },
        { metricName: '合同无风险召回率', metricValue: 0.87, metricType: 'judge' }
      ],
      gmtCreate: '2024-01-13 10:00:00',
      creator: 'lisi'
    },
    {
      id: 4,
      benchmarkType: '基线benchmark',
      benchmarkVersion: 'V202401121400',
      useCaseCount: 120,
      businessScenarioId: 4,
      businessScenarioName: '要素抽取-合同关键信息',
      remark: '要素抽取基线benchmark',
      metrics: [
        { metricName: '要素抽取准确率', metricValue: 0.89, metricType: 'judge' },
        { metricName: '要素抽取完整率', metricValue: 0.85, metricType: 'judge' }
      ],
      gmtCreate: '2024-01-12 14:00:00',
      creator: 'wangwu'
    }
  ];

  useEffect(() => {
    fetchBusinessScenarios();
    fetchAvailableMetrics();
    fetchData();
  }, [pagination.current, pagination.pageSize, searchParams]);
  
  const fetchAvailableMetrics = async () => {
    setMetricsLoading(true);
    try {
      const response = await benchmarkMetricApi.getList({ pageSize: 1000 });
      setAvailableMetrics(response.list || []);
    } catch (error) {
      console.error('获取指标列表失败:', error);
      setAvailableMetrics([]);
    } finally {
      setMetricsLoading(false);
    }
  };

  const fetchBusinessScenarios = async () => {
    try {
      const response = await businessScenarioApi.getList({ pageSize: 1000 });
      setBusinessScenarios(response.records || response.list || []);
    } catch (error) {
      console.error('获取业务场景列表失败:', error);
      // 使用模拟数据作为降级方案
      setBusinessScenarios(mockBusinessScenarios);
    }
  };

  const fetchData = async () => {
    if (!currentScenario) return;

    setLoading(true);
    try {
      const params = {
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        benchmarkVersion: searchParams.benchmarkVersion || undefined,
        benchmarkType: searchParams.benchmarkType || undefined,
        businessScenarioId: currentScenario.id, // 使用当前业务场景的ID
      };

      const response = await benchmarkApi.getList(params);
      
      setDataSource(response.records || response.list || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
      }));
    } catch (error) {
      console.error('获取Benchmark列表失败:', error);
      // 使用模拟数据作为降级方案
      setDataSource(mockData);
      setPagination(prev => ({
        ...prev,
        total: mockData.length,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchParams(prev => ({ ...prev, benchmarkVersion: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setModalVisible(true);
    form.resetFields();
    // 设置默认版本号
    const now = new Date();
    const version = `V${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
    form.setFieldsValue({ benchmarkVersion: version });
  };

  const handleEdit = async (record) => {
    setEditingRecord(record);
    setModalVisible(true);
    
    // 解析附件信息
    let attachmentFileList = [];
    if (record.attachmentInfo) {
      try {
        const attachmentData = typeof record.attachmentInfo === 'string' 
          ? JSON.parse(record.attachmentInfo) 
          : record.attachmentInfo;
        
        if (Array.isArray(attachmentData)) {
          attachmentFileList = attachmentData.map((item, index) => ({
            uid: String(item.attachmentId || index),
            name: item.attachmentName || '附件文件',
            status: 'done',
            attachmentId: item.attachmentId
          }));
        }
      } catch (e) {
        console.error('解析附件信息失败:', e);
      }
    }
    
    setSelectedFiles(attachmentFileList);
    
    // 第一步：根据任务执行历史ID获取评测场景的指标配置（应该有的指标）
    let scenarioMetrics = [];
    if (record.taskExecutionHistoryId) {
      try {
        const scenarioMetricsResponse = await benchmarkApi.getMetricsByExecutionHistory(record.taskExecutionHistoryId);
        scenarioMetrics = scenarioMetricsResponse || [];
      } catch (error) {
        console.error('获取评测场景指标配置失败:', error);
        message.warning('无法获取评测场景的指标配置');
      }
    }
    
    // 第二步：获取该Benchmark当前的指标配置（实际有的指标）
    let currentBenchmarkMetrics = [];
    try {
      const metricsResponse = await benchmarkMetricApi.getList({ 
        benchmarkId: record.id,
        pageSize: 100 
      });
      currentBenchmarkMetrics = metricsResponse.list || [];
    } catch (error) {
      console.error('获取Benchmark指标配置失败:', error);
    }
    
    // 第三步：对比两个列表，生成同步操作
    const metricsToDelete = [];
    const metricsToAdd = [];
    
    // 找出需要删除的指标（在currentBenchmarkMetrics中有，但在scenarioMetrics中没有）
    currentBenchmarkMetrics.forEach(currentMetric => {
      const existsInScenario = scenarioMetrics.some(
        scenarioMetric => scenarioMetric.metricName === currentMetric.metricName
      );
      if (!existsInScenario) {
        metricsToDelete.push(currentMetric.id);
      }
    });
    
    // 找出需要新增的指标（在scenarioMetrics中有，但在currentBenchmarkMetrics中没有）
    scenarioMetrics.forEach(scenarioMetric => {
      const existsInCurrent = currentBenchmarkMetrics.some(
        currentMetric => currentMetric.metricName === scenarioMetric.metricName
      );
      if (!existsInCurrent) {
        metricsToAdd.push({
          benchmarkId: record.id,
          metricName: scenarioMetric.metricName,
          metricType: scenarioMetric.metricType
        });
      }
    });
    
    // 保存同步操作信息，在保存时执行
    setEditingRecord({
      ...record,
      metricsToDelete,
      metricsToAdd
    });
    
    // 第四步：在表单中展示评测场景的指标列表（作为标准）
    // 过滤掉 metricAle 字段不为空的指标
    const displayMetrics = scenarioMetrics
      .filter(scenarioMetric => !scenarioMetric.metricAle)
      .map(scenarioMetric => {
        // 尝试从当前指标中找到对应的指标值
        const currentMetric = currentBenchmarkMetrics.find(
          m => m.metricName === scenarioMetric.metricName
        );

        return {
          metricId: currentMetric?.id || null,
          metricName: scenarioMetric.metricName,
          metricValue: currentMetric?.metricValue || 0,
          metricType: scenarioMetric.metricType
        };
      });
    
    form.setFieldsValue({
      benchmarkType: record.benchmarkType,
      benchmarkVersion: record.benchmarkVersion,
      useCaseCount: record.useCaseCount,
      businessScenarioId: record.businessScenarioId,
      remark: record.remark,
      metrics: displayMetrics,
      attachments: attachmentFileList
    });
  };

  const handleDelete = async (id) => {
    try {
      await benchmarkApi.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      console.error('删除Benchmark失败:', error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // 如果是编辑模式，先执行指标同步操作
      if (editingRecord && editingRecord.metricsToDelete && editingRecord.metricsToAdd) {
        try {
          // 第一步：删除多余的指标
          for (const metricId of editingRecord.metricsToDelete) {
            await benchmarkMetricApi.delete(metricId);
          }
          
          // 第二步：新增缺失的指标
          for (const metricData of editingRecord.metricsToAdd) {
            await benchmarkMetricApi.create(metricData);
          }
          
          if (editingRecord.metricsToDelete.length > 0 || editingRecord.metricsToAdd.length > 0) {
            message.success(`指标同步完成：删除 ${editingRecord.metricsToDelete.length} 个，新增 ${editingRecord.metricsToAdd.length} 个`);
          }
        } catch (error) {
          console.error('指标同步失败:', error);
          message.error('指标同步失败，但将继续保存Benchmark信息');
        }
      }
      
      // 构建附件信息
      let attachmentInfo = null;
      
      // 从已上传的文件列表中提取附件信息
      if (selectedFiles && selectedFiles.length > 0) {
        // 只保留已成功上传的文件（status为done且有attachmentId）
        const validFiles = selectedFiles.filter(file => 
          file.status === 'done' && file.attachmentId
        );
        
        if (validFiles.length > 0) {
          // 构建正确的附件信息数组
          attachmentInfo = JSON.stringify(
            validFiles.map(file => ({
              attachmentName: file.attachmentName || file.name,
              attachmentId: file.attachmentId
            }))
          );
        }
      }
      
      const submitData = {
        ...values,
        attachmentInfo: attachmentInfo
      };
      
      // 移除不需要提交的字段
      delete submitData.attachments;
      
      if (editingRecord) {
        await benchmarkApi.update(editingRecord.id, submitData);
        message.success('修改成功');
      } else {
        await benchmarkApi.create(submitData);
        message.success('新增成功');
      }
      
      setModalVisible(false);
      setSelectedFiles([]);
      setUploadProgress({
        uploading: false,
        current: 0,
        total: 0,
        percent: 0
      });
      fetchData();
    } catch (error) {
      console.error('保存Benchmark失败:', error);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination(paginationConfig);
  };

  // 手动触发批量上传
  const handleStartUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0 || isUploading) {
      message.warning('请先选择要上传的文件');
      return;
    }

    // 过滤掉已经上传成功的文件
    const filesToUpload = selectedFiles.filter(file => file.status !== 'done' || !file.attachmentId);
    
    if (filesToUpload.length === 0) {
      message.info('所有文件已上传完成');
      return;
    }

    setIsUploading(true);
    setUploadProgress({
      uploading: true,
      current: 0,
      total: filesToUpload.length,
      percent: 0
    });

    const uploadResults = [];
    let successCount = 0;
    let failCount = 0;

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        
        try {
          const uploadFormData = new FormData();
          const fileToUpload = file.originFileObj || file;
          uploadFormData.append('file', fileToUpload);
          
          // 调用上传接口，返回格式：{ attachmentName: "xxx", attachmentId: 123 }
          const uploadResponse = await benchmarkApi.uploadFile(uploadFormData);
          
          if (uploadResponse && uploadResponse.attachmentId) {
            uploadResults.push({
              success: true,
              attachmentId: uploadResponse.attachmentId,
              attachmentName: uploadResponse.attachmentName,
              fileName: file.name,
              message: '上传成功'
            });
            successCount++;
            
            // 更新文件状态，保存完整的附件信息
            setSelectedFiles(prev => prev.map(f => {
              if (f.uid === file.uid) {
                return {
                  uid: f.uid,
                  name: uploadResponse.attachmentName,
                  status: 'done',
                  attachmentId: uploadResponse.attachmentId,
                  attachmentName: uploadResponse.attachmentName
                };
              }
              return f;
            }));
          } else {
            throw new Error('上传失败：未返回附件ID');
          }
        } catch (error) {
          uploadResults.push({
            success: false,
            fileName: file.name,
            message: error.message || '上传失败'
          });
          failCount++;
          
          setSelectedFiles(prev => prev.map(f => 
            f.uid === file.uid ? { ...f, status: 'error' } : f
          ));
        }

        const current = i + 1;
        const percent = Math.round((current / filesToUpload.length) * 100);
        setUploadProgress({
          uploading: true,
          current,
          total: filesToUpload.length,
          percent
        });
      }

      setUploadProgress({
        uploading: false,
        current: filesToUpload.length,
        total: filesToUpload.length,
        percent: 100
      });

      if (successCount > 0) {
        message.success(`批量上传完成：成功 ${successCount} 个，失败 ${failCount} 个`);
      }

      if (failCount > 0) {
        const failedFiles = uploadResults.filter(result => !result.success);
        message.warning({
          content: (
            <div>
              <div className="mb-2">部分文件上传失败：</div>
              <div className="text-red-600 text-sm">
                {failedFiles.map((result, index) => (
                  <div key={index}>• {result.fileName}: {result.message}</div>
                ))}
              </div>
            </div>
          ),
          duration: 6,
        });
      }

      form.setFieldsValue({
        attachments: {
          uploadResults: uploadResults,
          fileList: selectedFiles
        }
      });

    } catch (error) {
      setUploadProgress({
        uploading: false,
        current: 0,
        total: 0,
        percent: 0
      });
      message.error('批量上传失败，请重试');
      console.error('批量上传失败:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const attachmentUploadProps = {
    name: 'attachments',
    multiple: true,
    accept: '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.csv',
    fileList: selectedFiles,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(`文件 ${file.name} 大小不能超过10MB`);
        return false;
      }
      return false;
    },
    maxCount: 20,
    onChange: (info) => {
      if (info.fileList.length > 20) {
        message.warning('最多只能上传20个文件');
        info.fileList = info.fileList.slice(0, 20);
      }
      
      setSelectedFiles(info.fileList);
      
      form.setFieldsValue({
        attachments: {
          fileList: info.fileList
        }
      });
    },
  };

  const getBenchmarkTypeColor = (type) => {
    const colorMap = {
      '基线benchmark': 'blue',
      '样本验收benchmark': 'green',
      '线上周期评测benchmark': 'orange'
    };
    return colorMap[type] || 'default';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Benchmark类型',
      dataIndex: 'benchmarkType',
      key: 'benchmarkType',
      width: 150,
      render: (type) => (
        <Tag color={getBenchmarkTypeColor(type)}>{type}</Tag>
      ),
    },
    {
      title: '版本号',
      dataIndex: 'benchmarkVersion',
      key: 'benchmarkVersion',
      width: 150,
      render: (version) => (
        <Tag color="geekblue">{version}</Tag>
      ),
    },
    {
      title: '业务场景',
      dataIndex: 'businessScenarioName',
      key: 'businessScenarioName',
      width: 180,
      render: (name) => (
        <Tag color="purple">{name}</Tag>
      ),
    },
    {
      title: '用例数量',
      dataIndex: 'useCaseCount',
      key: 'useCaseCount',
      width: 100,
      render: (count) => (
        <span className="font-medium text-blue-600">{count}</span>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (time) => formatDateTime(time),
    },
    {
      title: '创建人',
      dataIndex: 'creatorInfo',
      key: 'creator',
      width: 150,
      render: (creatorInfo) => {
        if (!creatorInfo) return '-';

        const { name, workNo, nickName } = creatorInfo;
        const displayName = name || nickName || '';
        const link = workNo ? `https://work.alibaba-inc.com/nwpipe/u/${workNo}` : '';

        if (link && displayName) {
          return (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700"
            >
              {displayName}{workNo ? `(${workNo})` : ''}
            </a>
          );
        }

        return displayName || '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 270,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<BarChartOutlined />}
            onClick={() => window.open(`#/benchmark/${record.id}`, '_blank')}
            style={{
              background: '#E5F5FF',
              border: '1px solid #91D5FF',
              color: '#1890FF'
            }}
          >
            详情
          </Button>
          {hasManagePermission() && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{
                background: '#E5F5FF',
                border: '1px solid #91D5FF',
                color: '#1890FF'
              }}
            >
              编辑
            </Button>
          )}
          {hasManagePermission() && (
            <Popconfirm
              title="确定要删除这个Benchmark吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                size="small"
                icon={<DeleteOutlined />}
                className="table-action-btn-delete"
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Benchmark管理</h1>
        <p className="page-description">管理评测基准数据，包括基线、验收和线上周期评测的benchmark</p>
      </div>

      <div className="mb-4">
        <Space size="middle">
          <Search
            placeholder="请输入版本号"
            allowClear
            suffix={<SearchOutlined />}
            size="large"
            className="w-60"
            onSearch={handleSearch}
          />
          <Select
            placeholder="Benchmark类型"
            allowClear
            size="large"
            className="w-48"
            onChange={(value) => setSearchParams(prev => ({ ...prev, benchmarkType: value }))}
          >
            {benchmarkTypes.map(type => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Select>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={editingRecord ? '编辑Benchmark' : '新建Benchmark'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={800}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-4"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="benchmarkType"
                label="Benchmark类型"
                rules={[
                  { required: true, message: '请选择Benchmark类型' },
                ]}
              >
                <Select placeholder="请选择Benchmark类型">
                  {benchmarkTypes.map(type => (
                    <Option key={type} value={type}>
                      {type}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="benchmarkVersion"
                label="Benchmark版本"
                rules={[
                  { required: true, message: '请输入Benchmark版本' },
                ]}
              >
                <Input placeholder="请输入Benchmark版本" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="businessScenarioId"
                label="业务场景"
                rules={[
                  { required: true, message: '请选择业务场景' },
                ]}
              >
                <Select placeholder="请选择业务场景">
                  {(businessScenarios || []).map(scenario => (
                    <Option key={scenario.id} value={scenario.id}>
                      {scenario.scenarioName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="useCaseCount"
                label="评测集用例数量"
                rules={[
                  { required: true, message: '请输入用例数量' },
                  { type: 'number', min: 1, message: '用例数量必须大于0' },
                ]}
              >
                <InputNumber 
                  placeholder="请输入用例数量" 
                  className="w-full"
                  min={1}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="remark"
            label="备注"
          >
            <TextArea 
              rows={3} 
              placeholder="请输入备注信息" 
              maxLength={500}
              showCount
            />
          </Form.Item>
          
          <Form.Item
            name="attachments"
            label="附件上传"
            extra="支持上传多个文件，格式：PDF、DOC、DOCX、TXT、JPG、PNG、GIF、XLSX、XLS、CSV，单个文件大小不超过10MB"
          >
            <div>
              <Upload {...attachmentUploadProps}>
                <Button 
                  icon={<UploadOutlined />} 
                  disabled={uploadProgress.uploading}
                >
                  选择文件（支持多选）
                </Button>
              </Upload>
              
              {selectedFiles.length > 0 && (
                <div className="mt-3">
                  <Button 
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={handleStartUpload}
                    disabled={uploadProgress.uploading}
                    className="mr-2"
                  >
                    {uploadProgress.uploading ? '上传中...' : `开始上传 (${selectedFiles.length}个文件)`}
                  </Button>
                  <Button 
                    onClick={() => {
                      setSelectedFiles([]);
                      form.setFieldsValue({
                        attachments: {
                          fileList: []
                        }
                      });
                    }}
                    disabled={uploadProgress.uploading}
                  >
                    清空文件
                  </Button>
                </div>
              )}
            </div>
            
            {uploadProgress.uploading && (
              <div className="mt-3 p-3 bg-blue-50 rounded border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">
                    正在上传文件 ({uploadProgress.current}/{uploadProgress.total})
                  </span>
                  <span className="text-sm text-blue-600">
                    {uploadProgress.percent}%
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress.percent}%` }}
                  ></div>
                </div>
              </div>
            )}
          </Form.Item>
          
          <Form.List name="metrics">
            {(fields) => (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-medium">指标配置</span>
                  {metricsLoading && <span className="text-gray-500 text-sm">加载中...</span>}
                  {editingRecord && (
                    <span className="text-blue-600 text-sm">
                      来源：评测场景（任务执行历史ID: {editingRecord.taskExecutionHistoryId}）
                    </span>
                  )}
                </div>
                {fields.length === 0 && !metricsLoading && (
                  <div className="text-center py-8 text-gray-400">
                    暂无指标配置，请先在评测场景中配置指标
                  </div>
                )}
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" className="mb-4">
                    <Row gutter={16}>
                      <Col span={10}>
                        <Form.Item
                          {...restField}
                          name={[name, 'metricName']}
                          label="指标名称"
                        >
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'metricValue']}
                          label="指标值"
                          rules={[
                            { required: true, message: '请输入指标值' },
                            { type: 'number', message: '请输入有效的数字' }
                          ]}
                        >
                          <InputNumber 
                            placeholder="0.95" 
                            step={0.01}
                            precision={2}
                            className="w-full"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, 'metricType']}
                          label="指标类型"
                        >
                          <Select disabled placeholder="请选择">
                            <Option value="judge">评测指标</Option>
                            <Option value="process">过程指标</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
}

export default BenchmarkList;
